import { createRouter, createWebHistory } from 'vue-router'
import { ElMessage } from 'element-plus'
import store from '../store'
import i18n from '@/locales'
import { authApi } from '@/api'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/UploadHome.vue'),
    meta: { auth: 'user' }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/UserLogin.vue')
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/UserRegister.vue')
  },
  {
    // 「我的文件」：照片墙式文件列表页
    path: '/myfiles',
    name: 'myFiles',
    component: () => import('../views/PhotoWall.vue'),
    meta: { auth: 'user' }
  },
  {
    // 旧「照片墙」地址，兼容重定向
    path: '/photowall',
    redirect: '/myfiles'
  },
  {
    // 「历史记录」：上传历史记录页（独立页面，与「我的文件」分开维护）
    path: '/history',
    name: 'history',
    component: () => import('../views/History.vue'),
    meta: { auth: 'user' }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/AdminDashBoard.vue'),
    // 统一会话：任意有效登录（user / admin）均可进入文件管理，
    // 非管理员仅可见「文件管理」菜单，数据范围由后端按身份过滤
    meta: { auth: 'either' }
  },
  {
    path: '/userManage',
    name: 'userManage',
    component: () => import('../views/UserManage.vue'),
    meta: { auth: 'admin' }
  },
  {
    path: '/customerConfig',
    name: 'customerConfig',
    component: () => import('../views/CustomerConfig.vue'),
    meta: { auth: 'admin' }
  },
  {
    path: '/systemConfig',
    name: 'systemConfig',
    component: () => import('../views/SystemConfig.vue'),
    meta: { auth: 'admin' }
  },
  {
    // 开放接口文档：用户端/管理端/游客均可直接访问（文档不含任何凭据）
    path: '/apiDocs',
    name: 'apiDocs',
    component: () => import('../views/ApiDocs.vue')
  },
  {
    path: '/adminLogin',
    name: 'adminLogin',
    component: () => import('../views/AdminLogin.vue'),
  },
  {
    path: '/blockimg',
    name: 'blockimg',
    component: () => import('../views/BlockImage.vue'),
  },
  {
    path: '/pub/whiteliston',
    name: 'whiteliston',
    component: () => import('../views/WhiteListOn.vue'),
  },
  {
    path: '/pub/browse/:dir*',
    name: 'publicBrowse',
    component: () => import('../views/PublicBrowse.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('../views/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 全局认证守卫：带 meta.auth 的路由统一做会话检查
// authApi.checkSession 内部带并发去重，同一次导航/连续导航只发一次请求
router.beforeEach(async (to) => {
  const auth = to.meta.auth
  if (!auth) return true

  const redirectFor = (kind) => {
    const wasLoggedIn = !!store.state.authType
    const loginName = kind === 'admin' ? 'adminLogin' : 'login'
    store.commit('setAuthType', null)
    if (to.name !== loginName) {
      // 只有之前已登录（session 过期）才提示错误，首次未登录静默跳转
      if (wasLoggedIn) {
        ElMessage.error(i18n.global.t('login.authRequired'))
      }
      return { name: loginName }
    }
    return true
  }

  try {
    const data = await authApi.checkSession()

    // 统一会话：authType 是唯一身份来源，写入 store 供菜单/页面判断
    store.commit('setAuthType', data.valid ? data.authType : null)

    if (auth === 'admin') {
      // 管理区域：必须已配置管理员且当前为有效管理员会话
      // （管理员未配置时不再放行，需先在安全配置或环境变量中设置管理员凭据）
      if (data.valid && data.authType === 'admin' && data.adminRequired) {
        return true
      }
      return redirectFor('admin')
    }

    if (auth === 'either') {
      // 文件管理：任意有效登录会话（user / admin）均可进入
      if (data.valid && (data.authType === 'admin' || data.authType === 'user')) {
        return true
      }
      return redirectFor('user')
    }

    // auth === 'user'
    // 有有效 user 会话才视为已登录（admin 会话不算用户登录）
    // anonymousUpload 只代表允许匿名访问，不代表已登录
    const hasUserSession = data.valid && data.authType === 'user'
    if (data.anonymousUpload === true || hasUserSession) {
      return true
    }
    return redirectFor('user')
  } catch {
    // 会话检查请求失败，按未登录处理
    return redirectFor(auth)
  }
})

export default router
